import type { Configuration, RedirectRequest } from '@azure/msal-browser';

export type AuthProvider = 'INGKA' | 'IIGRS';

export interface AuthEnvironment {
  ingkaHostnames?: string;
  ingkaClientId?: string;
  ingkaAuthority?: string;
  ingkaRedirectUri?: string;
  ingkaAccessScope?: string;
  iigrsHostname?: string;
  iigrsClientId?: string;
  iigrsAuthority?: string;
  iigrsRedirectUri?: string;
  iigrsAccessScope?: string;
}

export interface RuntimeAuthConfig {
  provider: AuthProvider;
  hostname: string;
  msalConfig: Configuration;
  loginRequest: RedirectRequest;
  accessScope: string;
}

export class AuthConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthConfigurationError';
  }
}

const environment: AuthEnvironment = {
  ingkaHostnames: process.env.REACT_APP_INGKA_HOSTNAMES,
  ingkaClientId: process.env.REACT_APP_INGKA_AD_CLIENT_ID,
  ingkaAuthority: process.env.REACT_APP_INGKA_AD_AUTHORITY,
  ingkaRedirectUri: process.env.REACT_APP_INGKA_REDIRECT_URI,
  ingkaAccessScope: process.env.REACT_APP_INGKA_EXPORTER_ACCESS_SCOPE,
  iigrsHostname: process.env.REACT_APP_IIGRS_HOSTNAME,
  iigrsClientId: process.env.REACT_APP_IIGRS_AD_CLIENT_ID,
  iigrsAuthority: process.env.REACT_APP_IIGRS_AD_AUTHORITY,
  iigrsRedirectUri: process.env.REACT_APP_IIGRS_REDIRECT_URI,
  iigrsAccessScope: process.env.REACT_APP_IIGRS_EXPORTER_ACCESS_SCOPE,
};

const normalizeHostname = (hostname: string) =>
  hostname.trim().toLowerCase().replace(/\.$/, '');

const configuredHostnames = (value?: string) =>
  value
    ?.split(',')
    .filter(isConfigured)
    .map(normalizeHostname) ?? [];

const isConfigured = (value?: string): value is string =>
  Boolean(value?.trim()) && !value?.startsWith('REPL_APP_');

const requireConfigValue = (
  provider: AuthProvider,
  field: string,
  value?: string
) => {
  if (!isConfigured(value)) {
    throw new AuthConfigurationError(
      `${provider} authentication configuration is missing ${field}.`
    );
  }

  return value;
};

const createRuntimeConfig = (
  provider: AuthProvider,
  hostname: string,
  clientId: string | undefined,
  authority: string | undefined,
  redirectUri: string | undefined,
  accessScope: string | undefined
): RuntimeAuthConfig => {
  const resolvedClientId = requireConfigValue(provider, 'client ID', clientId);
  const resolvedAuthority = requireConfigValue(
    provider,
    'authority',
    authority
  );
  const resolvedRedirectUri = requireConfigValue(
    provider,
    'redirect URI',
    redirectUri
  );
  const resolvedAccessScope = requireConfigValue(
    provider,
    'access scope',
    accessScope
  );

  return {
    provider,
    hostname,
    msalConfig: {
      auth: {
        clientId: resolvedClientId,
        authority: resolvedAuthority,
        redirectUri: resolvedRedirectUri,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    },
    loginRequest: {
      scopes: [resolvedAccessScope],
    },
    accessScope: resolvedAccessScope,
  };
};

export const resolveAuthConfig = (
  hostname: string,
  env: AuthEnvironment
): RuntimeAuthConfig => {
  const requestedHostname = normalizeHostname(hostname);
  const ingkaHostnames = configuredHostnames(env.ingkaHostnames);
  const iigrsHostnames = configuredHostnames(env.iigrsHostname);
  const duplicateHostname = ingkaHostnames.find(hostname =>
    iigrsHostnames.includes(hostname)
  );

  if (duplicateHostname) {
    throw new AuthConfigurationError(
      `INGKA and IIGRS cannot use the same hostname "${duplicateHostname}".`
    );
  }

  if (ingkaHostnames.includes(requestedHostname)) {
    return createRuntimeConfig(
      'INGKA',
      requestedHostname,
      env.ingkaClientId,
      env.ingkaAuthority,
      env.ingkaRedirectUri,
      env.ingkaAccessScope
    );
  }

  if (iigrsHostnames.includes(requestedHostname)) {
    return createRuntimeConfig(
      'IIGRS',
      requestedHostname,
      env.iigrsClientId,
      env.iigrsAuthority,
      env.iigrsRedirectUri,
      env.iigrsAccessScope
    );
  }

  throw new AuthConfigurationError(
    `No authentication provider is configured for hostname "${requestedHostname}".`
  );
};

let runtimeAuthConfig: RuntimeAuthConfig | undefined;

export const getRuntimeAuthConfig = () => {
  runtimeAuthConfig ??= resolveAuthConfig(
    window.location.hostname,
    environment
  );
  return runtimeAuthConfig;
};
